import { AfterViewInit, Component, ElementRef, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';
import { AbstractComponent, FocusableComponent } from '../../abstract.component';
import { SolSelect } from '../../../models/core/sol-select';
import { SolLayout } from '../../../models/core/sol-layout';
import { SolSelectValue } from '../../../models/core/sol-select-value';
import { SolIcon } from '@sol/icons';
import { SolSearchFieldComponent } from '../sol-search-field/sol-search-field.component';
import { SolButtonComponent } from '../sol-button/sol-button.component';
import { SolButtonType } from '../../../models/core/sol-button';
import { SolViewComponent, SolViewData } from '../../view/sol-view/sol-view.component';
import { SolViewOptions } from '../../../models/core/sol-view-options';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { SolText } from '../../../models/core/sol-text';
import { SolType } from '../../../models/core/sol-type';
import { SolCheckboxComponent } from '../sol-checkbox/sol-checkbox.component';
import { BrowserTools } from '../../../tools/browser-tools';
import { ObjectTools } from '../../../tools/object-tools';
import { SolPopoverMenuContentComponent } from '../../popover/sol-popover-menu-content/sol-popover-menu-content.component';
import { SolMenuGroup } from '../../popover/sol-popover-menu-content/sol-popover-menu-content.state';
import { SelectTools } from '../../../tools/select-tools';

export interface SolSelectContentCallback {
  onSearch: (query: string) => void;
  onSelect: (id: string | undefined, close: boolean) => void;
  onMoveHighlight: (step: 1 | -1) => void;
  onEnter: () => void;
  onEscape: () => void;
  onClear: () => void;
  onAddValue: () => void;
  onMenuAction: (actionId: string) => void;
}

interface SolSelectContentItem<V> {
  id: string | undefined;
  optionId: string | undefined;
  viewData: SolViewData<V, SolViewOptions>;
  info?: SolText;
  infoBelow: boolean;
  disabled: boolean;
  active: boolean;
  selected: boolean;
  showCheckbox: boolean;
}

class SolSelectContentState<V> {
  private constructor(
    // Input
    public readonly select: SolSelect<V>,

    // Computed
    public readonly searchText: string,
    public readonly items: SolSelectContentItem<V>[],
    public readonly hasResults: boolean,
    public readonly loading: boolean,
    public readonly debounce: boolean,
    public readonly showClear: boolean,
    public readonly listboxId: string | undefined,
    public readonly multi: boolean,
    public readonly footerMenu: SolMenuGroup[],
  ) {}

  public static of<V>(): SolSelectContentState<V> {
    return new SolSelectContentState<V>(
      SolSelect.of<any>(SolType.string),
      '',
      [],
      false,
      false,
      false,
      false,
      undefined,
      false,
      [],
    ).recalculate();
  }

  public withSelect(select: SolSelect<V>): SolSelectContentState<V> {
    if (ObjectTools.equals(this.select, select) && this.searchText === select.query) {
      return this;
    }
    return this.copy({
      select,
      searchText: select.query,
    }).recalculate();
  }

  public withSearchText(searchText: string): SolSelectContentState<V> {
    if (this.searchText === searchText) {
      return this;
    }
    return this.copy({ searchText });
  }

  private recalculate(): SolSelectContentState<V> {
    const select = this.select;
    const loading = select.loading;
    const debounce = select.debounceMs > 0;
    const values = select.values;
    const isEmptySelection = values.length === 1 && values[0] === undefined;
    const selected = isEmptySelection ? new Set<string | undefined>() : new Set(values);
    const items = select.visibleAvailableValues.map(value => ({
      id: value.id,
      optionId: SelectTools.toOptionId(select.listboxId, value.id),
      info: value.info,
      infoBelow: !!value.info && value.infoPosition === 'below',
      disabled: loading || value.disabled === true,
      active: select.currentResultId !== undefined && value.id === select.currentResultId,
      selected: selected.has(value.id),
      viewData: SolSelectContentState.toViewData(select, value),
      showCheckbox: select.mode === 'multi',
    }));
    const isMulti = select.mode === 'multi';
    const showClear = isMulti && select.hasSelection;
    return this.copy({
      items,
      hasResults: items.length > 0,
      loading,
      debounce,
      showClear,
      listboxId: select.listboxId,
      multi: isMulti,
      footerMenu: SolSelectContentState.toFooterMenu(select),
    });
  }

  private copy(props: Partial<SolSelectContentState<V>>): SolSelectContentState<V> {
    return ObjectTools.copy<SolSelectContentState<V>>(this, props);
  }

  /**
   * The consumer's own footer menu, with the add-value action appended for an editable select so a
   * value the list does not carry is reachable from the same place every other action lives.
   */
  private static toFooterMenu<V>(select: SolSelect<V>): SolMenuGroup[] {
    if (!select.editable) {
      return select.menu;
    }
    const addValue: SolMenuGroup = {
      items: [{ id: SelectTools.addValueActionId, name: 'sol-select.add-value', icon: SolIcon.plus }],
    };
    return [...select.menu, addValue];
  }

  private static toViewData<V>(select: SolSelect<V>, value: SolSelectValue<V>): SolViewData<V, SolViewOptions> {
    return {
      dataType: select.dataType,
      viewId: select.viewId,
      mode: 'read',
      value: value.name,
      viewOptions: {
        ...select.viewOptions,
        ...(value.info && value.infoPosition === 'below'
          ? { layout: (select.viewOptions.layout ?? SolLayout.of().withGap('0')).withPy('0') }
          : {}),
      },
    };
  }
}

@Component({
  selector: 'sol-select-content',
  host: {
    tabindex: '0',
    '(select)': 'onNativeSelect($event)',
    '(keydown.arrowdown)': 'onArrow(1, $event)',
    '(keydown.arrowup)': 'onArrow(-1, $event)',
    '(keydown.enter)': 'onEnter($event)',
    '(keydown.space)': 'onSpace($event)',
    '(keydown.escape)': 'onEscape($event)',
    'data-shortcut-ignore': '',
    role: 'combobox',
    'aria-expanded': 'true',
    '[attr.aria-controls]': 'listboxId$()',
    '[attr.aria-activedescendant]': 'activeOptionId$()',
  },
  standalone: true,
  imports: [
    SolSearchFieldComponent,
    SolButtonComponent,
    SolViewComponent,
    TranslatePipe,
    SolCheckboxComponent,
    SolPopoverMenuContentComponent,
  ],
  templateUrl: './sol-select-content.component.html',
})
export class SolSelectContentComponent<V> extends AbstractComponent<SolSelectContentState<V>> implements AfterViewInit {
  protected readonly SolButtonType = SolButtonType;

  // Computed
  protected readonly searchText$ = this.compute(state => state.searchText);
  protected readonly items$ = this.compute(state => state.items);
  protected readonly hasResults$ = this.compute(state => state.hasResults);
  protected readonly loading$ = this.compute(state => state.loading);
  protected readonly debounce$ = this.compute(state => state.debounce);
  protected readonly showClear$ = this.compute(state => state.showClear);
  protected readonly searchable$ = this.compute(state => state.select.searchable);
  protected readonly footerMenu$ = this.compute(state => state.footerMenu);
  protected readonly listboxId$ = this.compute(state => state.listboxId);
  protected readonly activeOptionId$ = this.compute(state => state.select.activeOptionId);
  protected readonly multi$ = this.compute(state => state.multi);

  // Events
  @Output('search')
  private readonly searchEmitter = new EventEmitter<string>();
  @Output('select')
  private readonly selectEmitter = new EventEmitter<{
    id: string | undefined;
    close: boolean;
  }>();
  @Output('moveHighlight')
  private readonly moveHighlightEmitter = new EventEmitter<1 | -1>();
  @Output('enter')
  private readonly enterEmitter = new EventEmitter<void>();
  @Output('escape')
  private readonly escapeEmitter = new EventEmitter<void>();
  @Output('menuAction')
  private readonly menuActionEmitter = new EventEmitter<string>();

  private _callback: SolSelectContentCallback = {
    onSearch: query => this.searchEmitter.emit(query),
    onSelect: (id, close) => this.selectEmitter.emit({ id, close }),
    onMoveHighlight: step => this.moveHighlightEmitter.emit(step),
    onEnter: () => this.enterEmitter.emit(),
    onEscape: () => this.escapeEmitter.emit(),
    onClear: () => {},
    onAddValue: () => {},
    onMenuAction: actionId => this.menuActionEmitter.emit(actionId),
  };

  // Binding
  private readonly host = inject(ElementRef);
  @ViewChild('searchRef')
  private searchRef?: SolSearchFieldComponent;

  // Cache
  /** The dropdown borrows the popover's id; an embedded listbox has no popover to borrow one from and names itself. */
  private readonly embeddedListboxId = SelectTools.toListboxId(BrowserTools.newId());

  constructor() {
    super(SolSelectContentState.of<V>());
  }

  @Input()
  public set select(select: SolSelect<V>) {
    this.update(state => state.withSelect(select.withListboxId(select.listboxId ?? this.embeddedListboxId)));
  }

  public get callback(): SolSelectContentCallback {
    return this._callback;
  }

  @Input()
  public set callback(callback: SolSelectContentCallback) {
    this._callback = callback;
  }

  ngAfterViewInit(): void {
    this.focusable()?.focus(true);
  }

  protected onNativeSelect(event: unknown): void {
    if (event instanceof Event) event.stopPropagation();
  }

  protected onSearch(query: string): void {
    this.update(state => state.withSearchText(query));
    this.callback.onSearch(query);
  }

  protected onSelect(item: SolSelectContentItem<V>): void {
    const close = this.state.select.shouldCloseOnSelect();
    this.callback.onSelect(item.id, close);
  }

  protected onClear(): void {
    this.callback.onClear();
  }

  protected onMenuAction(actionId: string): void {
    if (actionId === SelectTools.addValueActionId) {
      this.callback.onAddValue();
      return;
    }
    this.callback.onMenuAction(actionId);
  }

  protected onArrow(step: 1 | -1, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.callback.onMoveHighlight(step);
  }

  protected onEnter(event: Event): void {
    const keyboard = event as KeyboardEvent;
    if (
      keyboard.ctrlKey ||
      keyboard.metaKey ||
      (event.target as HTMLElement).closest('button, sol-popover-menu-content')
    ) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.callback.onEnter();
  }

  protected onEscape(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.callback.onEscape();
  }

  protected onSpace(event: Event): void {
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
      return;
    }
    const current = this.state.select.currentResultId;
    if (!current) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const close = this.state.select.shouldCloseOnSelect();
    this.callback.onSelect(current, close);
  }

  protected override focusable(): FocusableComponent | undefined {
    return this.searchRef ?? BrowserTools.toFocusable(this.host);
  }
}
